import {Button} from '@wandb/weave/components/Button';
import {Checkbox} from '@wandb/weave/components/Checkbox';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import React from 'react';
import {useParams} from 'react-router';
import {Dropdown} from 'semantic-ui-react';

import {useProjectFieldsQuery} from '../../generated/graphql';
import {propagateErrorsContext} from '../../util/errors';
import {
  displayKey,
  getDefaultAbsoluteDateValue,
  getKeyAllowedOps,
  isValidFilter,
} from '../../util/filters';
import {EMPTY_FILTER_VALUE} from '../../util/filtersDefaults';
import {
  type FilterKey,
  type FilterMeta,
  type IndividualFilter,
  type IndividualOp,
} from '../../util/filterTypes';
import * as Run from '../../util/runs';
import type * as RunTypes from '../../util/runTypes';
import {FilterTagInfiniteDropdown} from '../InfiniteScrollDropdown/FilterTagComponents';
import {getCurrentSelections} from '../InfiniteScrollDropdown/utils';
import {getProjectFieldsQueryVariables} from '../ProjectFieldSelector';
import {useEditableFilterContext} from './FilterContext';
import {type FilterKeySelectorCreatorProps} from './FilterKeySelector';
import {type FilterValueSelectorCreatorProps} from './FilterValueSelector';
import {InvalidFilterTooltip} from './InvalidFilterTooltip';

const TAG_KEY_PLACEHOLDER = 'tags:-';

const TAG_OP_OPTIONS = [
  {text: 'is', value: '='},
  {text: 'is not', value: '!='},
  {text: 'in', value: 'IN'},
  {text: 'not in', value: 'NIN'},
] as const;

// the existing prod layout flexes the grid based on the value of the
// op selector, we'll fix this later
const LEGACY_TAG_GRID_CLASS =
  'group relative grid grid-cols-[24px_224px_100px_400px_32px] gap-x-8 items-start';

const V2_TAG_GRID_CLASS =
  'group relative grid grid-cols-[24px_100px_224px_100px_minmax(400px,600px)_32px] gap-x-8 items-center';

/**
 * The <style> tag here is also SO GROSS but I needed a way to selectively overwrite the CSS coming in by default so that I could dump the margins for Grid w/out having the refactor the original layout.
 */
const CUSTOM_TAG_FILTER_CSS = `.custom-tag-filter .filter-list__key {
  margin-left: 0px;
}
.custom-tag-filter .filter-list__operation {
  margin-left:0px;
}
.custom-tag-filter--legacy [data-test="filter-toggle"] {
  margin-top: 14px;
}
.custom-tag-filter--legacy [data-test="filter-delete"] {
  margin-top: 8px;
}
`;

const OperatorUserLabels = new Map<IndividualOp, string>([
  ['=', '='],
  ['!=', '\u2260'],
  ['>=', '\u2265'],
  ['<=', '\u2264'],
  ['IN', 'IN'],
  ['NIN', 'NOT IN'],
  ['CONTAINS', 'CONTAINS'],
  ['WITHINSECONDS', 'within last'],
]);

interface FilterEditableProps {
  filter: IndividualFilter;
  filterIndex: number;
  justAdded: boolean;

  /** Optional content at the start of the row (e.g. "WHERE" or AND/OR dropdown). When set, checkbox is shown to the left of the leading content. */
  leadingContent?: React.ReactNode;

  setFilter(filter: IndividualFilter): void;
  setFilterOp(op: IndividualOp): void;
  setFilterValue(value: RunTypes.Value): void;
  setFilterMultiValue(value: RunTypes.Value[]): void;
  setFilterMeta(meta: FilterMeta): void;
  setFilterDisabled(disabled: boolean): void;
  deleteFilter(): void;

  filterKeySelector(props: FilterKeySelectorCreatorProps): React.ReactNode;
  filterValueSelector(
    props: FilterValueSelectorCreatorProps<FilterKey>
  ): React.ReactNode;
}

const getOperatorDisplayList = (
  filterKey: FilterKey,
  keyType: string | undefined
) =>
  getKeyAllowedOps(filterKey, keyType).map(op => {
    const opLabel = OperatorUserLabels.get(op);
    if (!opLabel) {
      throw new Error('OperatorUserLabels missing op: ' + op);
    }
    const isSymbol = opLabel.length === 1;
    return {
      text: isSymbol ? <span className="symbol">{opLabel}</span> : opLabel,
      value: op,
    };
  });

export const FilterEditable = ({
  filter,
  justAdded,
  leadingContent,
  setFilter,
  setFilterOp,
  setFilterDisabled,
  deleteFilter,
  filterKeySelector,
  filterValueSelector,
  filterIndex,
}: FilterEditableProps) => {
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();
  const [{filter: contextFilter, isMultiValueFilter}, filterActions] =
    useEditableFilterContext(filterIndex);
  const gqlVars = getProjectFieldsQueryVariables({
    entityName: entityName,
    projectName: projectName,
    pattern: filter.key.name, // we only want to get the key type for the current filter's key. it may or may not have been queried already by the ProjectFieldSelector.
  });
  const {data} = useProjectFieldsQuery({
    variables: gqlVars,
    context: propagateErrorsContext(), // we don't care about error or loading here, it's ok if we don't know the key type.
  });
  const filterKeyType = data?.project?.fields?.edges?.[0]?.node?.type;

  const operatorDisplayList = getOperatorDisplayList(filter.key, filterKeyType);

  const isValid = isValidFilter(filter);

  const checkboxCell = (
    <div className="flex h-24 w-24 shrink-0 items-center justify-center">
      {!isValid ? (
        <InvalidFilterTooltip />
      ) : (
        <Checkbox
          size="small"
          data-test="filter-toggle"
          checked={!filter.disabled}
          // Only toggle if all fields are set.
          onCheckedChange={() => {
            const newActive = !filter.disabled;
            setFilterDisabled(newActive);
          }}
        />
      )}
    </div>
  );

  // @ts-ignore because this feature is operating on an extreme subsection of the inherited types and those types be nasty
  const selections = getCurrentSelections(contextFilter);
  // This supports two actions based on the kind of filter:
  const toggleFilter = React.useCallback(
    (newSelection: string) => {
      const selectionSet = new Set(selections);

      // Individual filters will replace that item with the new item
      if (!isMultiValueFilter) {
        if (selectionSet.has(newSelection)) {
          filterActions.setFilterValueIndividual('');
        } else {
          filterActions.setFilterValueIndividual(newSelection);
        }
      }

      // the multi-value filter will add or subtract based on if it's already included
      else {
        if (selectionSet.has(newSelection)) {
          selectionSet.delete(newSelection);
        } else {
          selectionSet.add(newSelection);
        }
        filterActions.setFilterValueMulti([...selectionSet]);
      }
    },
    // eslint-disable-next-line
    [contextFilter, filterActions, selections]
  );
  /**
   * The general idea here is that I want to hijack the rendering under the following conditions:
   * 1. It's a tag filter
   * 2. It's an org in the feature flag (we're rolling this out to OpenAI before it goes wider)
   *
   * So this is basically a copy/paste job of the default rendering code with some slight modifications.
   *
   * The CSS for the regular filter layout is pretty gross, so this is a hack to create an identical sized layout without having to muck about with all the spacing complexity affected by components. Should we refactor the other layout to use something nice and clean like CSS Grid where spacing between the columns is a function of the layout? Yes. Should I avoid expanding the scope of this patch to do that? Also yes.
   *
   * It also gives me unique instances of all the subcomponents so I can monkeypatch them as much as I desire to avoid creating regression risk by modifying functionality higher up
   */
  const isV2 = leadingContent != null;

  if (filter.key.section === 'tags') {
    const tagGridClass = isV2 ? V2_TAG_GRID_CLASS : LEGACY_TAG_GRID_CLASS;
    const tagModifierClass = isV2 ? '' : 'custom-tag-filter--legacy';

    // we are hardcoding the keyValue and storedKey to keep the selector from losing context
    // when I clear out the values: https://weightsandbiases.slack.com/archives/C041GF3LB1Q/p1692367577594179
    const tagKeyColumn = filterKeySelector({
      focusOnMount: justAdded,
      keyValue: TAG_KEY_PLACEHOLDER,
      storedKey: TAG_KEY_PLACEHOLDER,
      onValidSelection: keyString => {
        // when the key is changed we disable the filter automatically and default it
        const filterKey = Run.keyFromString(keyString);
        if (filterKey != null) {
          setFilter({
            ...filter,
            key: filterKey,
            op: '=',
            value: true,
            disabled: true,
          });
        }
      },
    });

    return (
      <Tailwind>
        <div
          className={`${tagGridClass} custom-tag-filter ${tagModifierClass}`}
          data-test="filter-item">
          <style>{CUSTOM_TAG_FILTER_CSS}</style>
          {isV2 && checkboxCell}
          {isV2 ? leadingContent : checkboxCell}
          {tagKeyColumn}
          <Dropdown
            className="filter-dropdown filter-list__operation"
            data-test="filter-operation"
            options={[...TAG_OP_OPTIONS]}
            selectOnNavigation={false}
            value={filter.op}
            onChange={(e, {value}) => {
              // @ts-ignore
              filterActions.changeFilterOperation(value);
            }}
          />
          <FilterTagInfiniteDropdown
            entityName={entityName}
            projectName={projectName}
            isMultiSelect={isMultiValueFilter}
            // @ts-ignore safe, but revisit to figure out why overlap isn't happening correctly
            selections={getCurrentSelections(filter)}
            toggleSelection={toggleFilter}
          />
          <Button
            variant="ghost"
            icon="delete"
            data-test="filter-delete"
            onClick={deleteFilter}
            aria-label="Delete filter"
          />
        </div>
      </Tailwind>
    );
  }

  return (
    <Tailwind>
      <div data-test="filter-item" className="flex items-center gap-8">
        {isV2 && checkboxCell}
        {isV2 ? (
          <div className="w-[100px]">{leadingContent}</div>
        ) : (
          checkboxCell
        )}
        {filterKeySelector({
          isValid,
          focusOnMount: justAdded,
          keyValue: displayKey(filter.key),
          storedKey: displayKey(filter.key),
          onValidSelection: keyString => {
            const filterKey = Run.keyFromString(keyString);
            if (filterKey != null) {
              if (
                filterKey.section === 'run' &&
                filterKey.name === 'createdAt'
              ) {
                setFilter({
                  ...filter,
                  key: filterKey,
                  op: '<=',
                  value: getDefaultAbsoluteDateValue(),
                  disabled: false,
                });
              } else if (filterKey.section === 'tags') {
                setFilter({
                  ...filter,
                  key: filterKey,
                  op: '=',
                  value: true,
                  disabled: true,
                });
              } else {
                setFilter({
                  ...filter,
                  key: filterKey,
                  op: '=',
                  value: EMPTY_FILTER_VALUE,
                  disabled: true,
                });
              }
            }
          },
        })}
        <Dropdown
          className={clsx('filter-dropdown filter-list__operation', {
            invalid: !isValid,
          })}
          data-test="filter-operation"
          options={operatorDisplayList}
          placeholder={'operator'}
          selectOnNavigation={false}
          value={filter.op}
          onChange={(e, {value}) => {
            setFilterOp(value as IndividualOp);
          }}
        />
        {filterValueSelector({
          filter,
          setFilter,
          isValid,
        })}
        <Button
          variant="ghost"
          icon="delete"
          data-test="filter-delete"
          onClick={deleteFilter}
          aria-label="Delete filter"
        />
      </div>
    </Tailwind>
  );
};

export default React.memo(FilterEditable);
